import {
  Component,
  Inject,
  Input,
  OnInit,
  ViewEncapsulation
} from "@angular/core";
import { CmsData } from "@dotcom-frontend/data";
import {
  LgHeaderComponent,
  LgHeaderLogoComponent,
  LgIconComponent
} from "@legal-and-general/canopy";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { AnalyticsService } from "../../../services/analytics/analytics.service";
import { ComponentLoaderService } from "../../../services/component-loader/component-loader.service";
import { GlobalState } from "../../../store/global/global.reducer";
import {
  selectMainMobileNavOpen,
  selectPageTitle,
  selectPageType
} from "../../../store/global/global.selectors";
import { setMainNavMobileOpen } from "../../../store/global/global.actions";
import { DynamicComponentItem } from "../../utils/dynamic-loader/dynamic-component-item";
import { DynamicComponent } from "../../utils/dynamic-loader/dynamic.component";
import { LoaderComponent } from "../../utils/dynamic-loader/loader.component";
import { DOCUMENT, NgClass, NgTemplateOutlet } from "@angular/common";
import { HtmlTagService } from "../../../services/html-tag/html-tag.service";

@Component({
  selector: "dotcom-frontend-header",
  templateUrl: "./Header.component.html",
  styleUrl: "./Header.component.scss",
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    LgHeaderComponent,
    LgIconComponent,
    LoaderComponent,
    LgHeaderLogoComponent,
    NgClass,
    NgTemplateOutlet
  ]
})
export class HeaderComponent implements OnInit, DynamicComponent {
  @Input() data: CmsData | undefined;
  headerData: any | undefined;
  ctaButtons: any = [];
  isCampaignOrArticlePage: boolean | undefined;
  isLanguageHeader = false;
  isSticky = false;
  headerLogoUrl = "";
  headerLogoAltText = "";
  headerLogoImage = "";
  secondaryPartnerLogo = "";
  secondaryPartnerLogoAltText = "";
  pageTitle = "";
  pageType = "";
  pageTitle$: Observable<string> = this.store.select(selectPageTitle);
  pageType$: Observable<string> = this.store.select(selectPageType);
  navigationComponent: DynamicComponentItem | undefined;
  headerStyle = "compact";
  pageHeaderStyle = "";
  isCampaignPageHeader = false;
  audienceNavigation: DynamicComponentItem | undefined;
  mobileNav = false;
  isMobileNavOpen$: Observable<boolean> = this.store.select(
    selectMainMobileNavOpen
  );

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private store: Store<{ global: GlobalState }>,
    private analyticsService: AnalyticsService,
    private componentLoaderService: ComponentLoaderService,
    private htmlTagService: HtmlTagService
  ) {
    this.pageTitle$.subscribe((title) => {
      this.pageTitle = title;
    });
    this.pageType$.subscribe((pageType) => {
      this.pageType = pageType;
    });
    this.isMobileNavOpen$.subscribe((open) => {
      this.mobileNav = open;
    });
  }

  async ngOnInit() {
    if (this.data) {
      this.headerData = this.data["headerData"];
      this.headerStyle = this.headerData["headerStyle"];
      this.pageHeaderStyle = this.data["headerStyle"];
      this.isSticky = this.data["isSticky"];

      if (
        this.data["secondaryPartnerLogo"] &&
        this.pageHeaderStyle != "standard"
      ) {
        this.secondaryPartnerLogo = this.data["secondaryPartnerLogo"]["url"];
        this.secondaryPartnerLogoAltText =
          this.data["secondaryPartnerLogoAltText"];
      }

      if (
        this.pageHeaderStyle === 'campaign' ||
        this.pageHeaderStyle === 'sticky-campaign'
      ) {
        this.isCampaignPageHeader = true;
      }

      if (
        this.pageType === "CampaignPage" ||
        this.pageType === "ArticlePage"
      ) {
        this.isCampaignOrArticlePage = true;
      }

      if (
        this.headerData["availableLanguagesForPage"] &&
        this.headerData["availableLanguagesForPage"].length > 1
      ) {
        this.isLanguageHeader = true;
      }

      if (this.data["ctaButtons"]) {
        for (const button of this.data["ctaButtons"]) {
          const buttonComponent =
            await this.componentLoaderService.loadComponent(
              "Button",
              "globals",
              {
                ...button,
                adobeData: {
                  interactionContainer: "Header Block",
                  interactionLink: button["href"],
                  interactionName: button["text"],
                  interactionPage: this.pageTitle,
                  interactionRegion: "",
                  interactionType: "ButtonClick",
                  interactionDetails: ""
                }
              }
            );
          this.ctaButtons.push(buttonComponent);
        }
      }
    }

    if (this.headerData) {
      this.headerLogoUrl = this.headerData.headerLogoUrl;
      this.headerLogoAltText = this.headerData.headerLogoAltText;
      this.headerLogoImage = this.headerData.headerLogoImage;

      if (
        this.headerData.navRootReference?.expanded &&
        !this.isCampaignPageHeader
      ) {
        this.navigationComponent =
          await this.componentLoaderService.loadComponent(
            "Navigation",
            "globals",
            {
              navData: this.headerData.navRootReference,
              headerData: this.headerData
            }
          );
      }

      if (this.headerData.audienceNavigation) {
        this.audienceNavigation =
          await this.componentLoaderService.loadComponent(
            "AudienceNavigation",
            "globals",
            {
              audienceNavigation: this.headerData.audienceNavigation,
              allSitesLabel: this.headerData.allSitesLabel,
              allSiteLinks: this.headerData.allSiteLinks
            }
          );
      }

      this.setNavigationHeight();
    }
  }

  interactionClickEvent(clickUrl: any, clickName: any, clickDetails?: any) {
    this.analyticsService.adobeInteractionEvent(
      "Header Block",
      clickUrl,
      clickName,
      "",
      "ButtonClick",
      this.pageTitle,
      clickDetails || ""
    );
  }

  isTall() {
    if (!this.isCampaignPageHeader) {
      return this.headerStyle === "tall";
    } else {
      return this.headerStyle === "standard";
    }
  }
  toggleMobileNav(event: any) {
    event.stopPropagation();

    if (this.mobileNav) {
      this.store.dispatch(setMainNavMobileOpen({ open: false }));
      this.unLockBodyScroll();
    } else {
      this.store.dispatch(setMainNavMobileOpen({ open: true }));
      this.lockBodyScroll();
    }
  }

  lockBodyScroll() {
    this.document.body.classList.add("scroll-lock");
  }
  unLockBodyScroll() {
    this.document.body.classList.remove("scroll-lock");
  }

  setNavigationHeight() {
    let audienceHeightLg = 0;
    let audienceHeightSm = 0;
    let headerHeightLg = 88;
    const headerHeightSm = 88;

    if (this.headerData.audienceNavigation) {
      audienceHeightLg = 48;
      audienceHeightSm = 43;
    }

    if (this.isTall()) {
      headerHeightLg = 154;
    }

    this.htmlTagService.addTag({
      tag: "style",
      identifier: "nav-height",
      position: "body",
      text: `.navigation__height {
              height: calc(100svh - ${headerHeightSm}px - ${audienceHeightSm}px);
            }
            @media (min-width: 64rem) {
                .navigation__height {
                  height: calc(100svh - ${headerHeightLg}px - ${audienceHeightLg}px);
                }

                .navigation__height.navigation__background--top {
                  height: auto;
                }
            }`
    });
  }
}
